// mui
import { makeStyles } from "@material-ui/core";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Button,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ProjectDetailContext } from "../../../pages/ProjectDetail";
import {
  IMeasurementHandler,
  MeasurementType,
} from "../../../viewer/measurement/types";
import DeleteMeasurementResultDialog from "../../DeleteMeasurementResultDialog";

import Q3DIconButton from "../../Parts/Q3DIconButton";
import { HeatMapIcon } from "../../Parts/Q3DIcons";
import { DARK } from "../../config/componentsConfig";
import CalcContext from "../Contexts/CalcContext";

const SELECTED_ITEM_BGCOLOR_FOR_DARK = "#1D1C33";
const SELECTED_ITEM_BGCOLOR_FOR_LIGHT = "#E5E4FB";

interface MeasurementMainMenuProps {
  id: number;
  deleteMeasurementResult: (index: number) => void;
  type: MeasurementType;
  onClick: () => void;
  handlePickType: (v: string, selectedItem?: IMeasurementHandler) => void;
}

const MeasurementMainMenu = (props: MeasurementMainMenuProps) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<Element>(null); // menu anchor
  const [
    deleteMeasurementResultDialogDisplayed,
    setDeleteMeasurementResultDialogDisplayed,
  ] = useState<boolean>(false);
  const { setOnEditingMode } = useContext(CalcContext);

  const { t, i18n } = useTranslation();

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };
  const customBackground = {
    background: theme.palette.base.modal,
  };

  const openMeasurementMainMenu = (event: any) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const openDeleteMeasurementResultDialog = () => {
    setDeleteMeasurementResultDialogDisplayed(true);
    handleClose();
  };

  const closeDeleteMeasurementResultDialog = () => {
    setDeleteMeasurementResultDialogDisplayed(false);
    setMenuAnchorEl(null);
  };

  const handleClose = () => {
    setMenuAnchorEl(null);
  };

  type TList = {
    label: string;
    onClick: () => void;
  };
  const menuList = () => {
    const list = [] as TList[];
    if (props.type === MeasurementType.VOLUME_CALCULATION) {
      list.push({
        label: `${t("EDIT")}(${t("PICK_POINT")})`,
        onClick: () => {
          props.onClick();
          props.handlePickType("PICK_POINT");
          setOnEditingMode(true);
          setMenuAnchorEl(null);
        },
      });
      list.push({
        label: `${t("EDIT")}(${t("MOVE_XY")})`,
        onClick: () => {
          props.onClick();
          props.handlePickType("MOVE_XY");
          setOnEditingMode(true);
          setMenuAnchorEl(null);
        },
      });
      list.push({
        label: `${t("EDIT")}(${t("MOVE_Z")})`,
        onClick: () => {
          props.onClick();
          props.handlePickType("MOVE_Z");
          setOnEditingMode(true);
          setMenuAnchorEl(null);
        },
      });
    } else {
      list.push({
        label: t("EDIT"),
        onClick: () => {
          props.onClick();
          props.handlePickType("PICK_POINT");
          setOnEditingMode(true);
          setMenuAnchorEl(null);
        },
      });
    }
    list.push({
      label: t("REMOVE"),
      onClick: () => {
        setDeleteMeasurementResultDialogDisplayed(true);
        setMenuAnchorEl(null);
      },
    });
    return list;
  };

  return (
    <>
      <Button
        className="overflow4"
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={openMeasurementMainMenu}
      >
        <MoreVertIcon style={customColor} />
      </Button>
      <Menu
        id="simple-menu"
        keepMounted
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleClose}
        style={{ borderRadius: "0px" }}
        PaperProps={{
          className: "overflow6",
          style: { ...customBackground, ...customColor },
        }}
      >
        {menuList().map((list) => (
          <MenuItem
            onClick={list.onClick}
            style={{
              paddingTop: "0px",
              // borderBottom: "1px solid #1E1E1E",
              display: "flex",
              justifyContent: "space-between",
              paddingLeft: "5px",
              paddingRight: "0px",
            }}
          >
            <p
              style={{
                whiteSpace: "pre-line",
                textAlign: "left",
                width: "85%",
                margin: "0",
              }}
            >
              {list.label}
            </p>
          </MenuItem>
        ))}
      </Menu>

      {deleteMeasurementResultDialogDisplayed && (
        <DeleteMeasurementResultDialog
          deleteMeasurementResultDialogDisplayed={
            deleteMeasurementResultDialogDisplayed
          }
          closeDeleteMeasurementResultDialog={
            closeDeleteMeasurementResultDialog
          }
          id={props.id}
          deleteMeasurementResult={props.deleteMeasurementResult}
        />
      )}
    </>
  );
};

interface VolumeListProps {
  setOpenCalcResultDetailDialog: Dispatch<SetStateAction<boolean>>;
  setSelectedItem: Dispatch<SetStateAction<IMeasurementHandler>>;
  setEditedItem: Dispatch<SetStateAction<IMeasurementHandler>>;
  openCalcResultDetailDialog: boolean;
  resCalcType: (result: any) => void;
  selectedCalcResult: IMeasurementHandler[];
  setSelectedCalcResult: Dispatch<SetStateAction<any[]>>;
  selectedItem: IMeasurementHandler;
  handleClickCancel: () => void;
  handleSetSelectedCalcResult: (items: IMeasurementHandler[]) => void;
  deleteCalcResult: (deleteId: number) => Promise<void>;
  showHeatMap: boolean;
  handlePickType: (v: string, selectedItem?: IMeasurementHandler) => void;
  toggleSetShowHeatMap: (show?: boolean) => void;
}

// Click Event
let selectedValBool: boolean = false;
// List Item Selected
let listVariable: any = undefined;
// Click Visibility
let clickVisibility: any = undefined;

export const ProjectVolumeList = (volumeListProps: VolumeListProps) => {
  const { viewer, isJapanese } = useContext(ProjectDetailContext);

  const userCustomStyle = makeStyles(() => ({
    volumeList: {
      width: "calc(100% - 4px)",
      bgcolor: "#0D0D10",
      position: "absolute",
      overflow: "auto",
      height: "calc(100% - 4px)",
      margin: "auto",
      top: "2px",
      left: "2px",
      backgroundColor: "rgba(0, 0, 0, 0)",
      "& ul": { padding: 0 },
    },
    volumeListItem: {
      height: "28px",
      width: "100%",
      backgroundColor: "#0D0D10",
      marginBottom: "2px",
    },
    volumeListItemButton: {
      padding: "0px 0px 0px 5px",
      backgroundColor: "rgba(0, 0, 0, 0)",
      height: "100%",
      width: "100%",
    },
  }));

  useEffect(() => {
    if (!volumeListProps.selectedItem) {
      return;
    }

    selectItemHandler(volumeListProps.selectedItem);
  }, [volumeListProps.selectedItem]);

  // Click List Data
  const listItemArrayRef = useRef<any[]>([]);
  // Click Visibility
  const visibilityArrayRef = useRef<any[]>([]);

  const scrollSelectedDataMove = () => {
    let scrollRef: HTMLDivElement = null;
    if (listVariable !== undefined) {
      scrollRef = listItemArrayRef.current.find(
        (tagId) => tagId?.id === listVariable?.id
      );
      scrollRef?.scrollIntoView({ block: "end" });
    } else {
      scrollRef = visibilityArrayRef.current.find(
        (tagId) => tagId?.id === clickVisibility?.id
      );
      scrollRef?.scrollIntoView({ block: "end" });
    }
    listVariable = undefined;
    clickVisibility = undefined;
  };

  useEffect(() => {
    if (!selectedValBool) return;
    scrollSelectedDataMove();
    selectedValBool = false;
  }, [selectedValBool]);

  const deleteMeasurementResult = (idToRemove: number) => {
    volumeListProps.deleteCalcResult(idToRemove);
    volumeListProps.handleClickCancel();
  };

  // 計算結果(距離) 引数data:ViewerのcreateMeasutementの返却値
  const resDistance = (data: any) => {
    if (data === undefined) return;
    // 小数点第３位返却
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      const sum: number = data.value.fillVolume - data.value.cutVolume;
      return Math.round(sum * 1000) / 1000;
    } else {
      const value: number = data.value;
      return Math.round(value * 1000) / 1000;
    }
  };

  const resUnit = (data: any) => {
    if (data === undefined) return;
    const type: string = data.type;
    if (type === MeasurementType.VOLUME_CALCULATION) {
      return (
        <span>
          m<sup>3</sup>
        </span>
      );
    } else if (type === MeasurementType.FLAT_AREA_CALCULATION) {
      return (
        <span>
          m<sup>2</sup>
        </span>
      );
    } else {
      return <span>m</span>;
    }
  };

  useEffect(() => {
    const handleMeasurementClick = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { measurement } = customEvent.detail;
      if (!measurement._group.visible) return;

      const clickedId = measurement.id;

      if (volumeListProps.selectedItem?.id === clickedId) {
        viewer.setHeatMapVisibility(clickedId, false);
        volumeListProps.handleClickCancel();
      } else {
        selectedValBool = true;
        listVariable = e.currentTarget;
        volumeListProps.setSelectedItem(measurement);
        volumeListProps.setOpenCalcResultDetailDialog(true);
      }
    };

    window.addEventListener("measurement-click", handleMeasurementClick);
    return () => {
      window.removeEventListener("measurement-click", handleMeasurementClick);
    };
  }, [volumeListProps]);

  useEffect(() => {
    const handleMeasurementRightClick = (e: Event) => {
      const customEvent = e as CustomEvent<{ measurement: any }>;
      const { measurement } = customEvent.detail;

      if (!measurement._group.visible) return;

      viewer.setMeasurementVisibility(measurement.id, {
        text: false,
        measurement: false,
      });

      if (volumeListProps.selectedItem?.id === measurement.id) {
        viewer.setHeatMapVisibility(measurement.id, false);
      }

      volumeListProps.setSelectedCalcResult([
        ...volumeListProps.selectedCalcResult,
      ]);

      volumeListProps.handleSetSelectedCalcResult([
        ...volumeListProps.selectedCalcResult,
      ]);
    };

    window.addEventListener(
      "measurement-right-click",
      handleMeasurementRightClick
    );

    return () => {
      window.removeEventListener(
        "measurement-right-click",
        handleMeasurementRightClick
      );
    };
  }, [
    volumeListProps.selectedItem,
    volumeListProps.showHeatMap,
    volumeListProps.selectedCalcResult,
  ]);

  // リストアイテムをクリックした場合の制御
  const clickListItemEvent = (
    ev: React.MouseEvent<HTMLDivElement, MouseEvent>,
    result: any
  ) => {
    if (volumeListProps.selectedItem === result) {
      viewer.setHeatMapVisibility(result.id, false);
      volumeListProps.handleClickCancel();
      return;
    }
    selectedValBool = true;
    listVariable = ev.currentTarget;
    volumeListProps.setSelectedItem(result);
    volumeListProps.setOpenCalcResultDetailDialog(true);
  };

  const selectItemHandler = (result: any) => {
    viewer.setHeatMapVisibility(volumeListProps.selectedItem?.id, false);
    viewer.setMeasurementVisibility(result.id, {
      text: true,
      measurement: true,
    });

    viewer.changeAppearanceBySelected(result.id);

    if (result?.type === MeasurementType.VOLUME_CALCULATION) {
      if (volumeListProps.showHeatMap) {
        viewer.setHeatMapVisibility(result.id, true);
      }
    }
  };

  const selectedItemColor = () => {
    return theme.palette.mode === DARK
      ? SELECTED_ITEM_BGCOLOR_FOR_DARK
      : SELECTED_ITEM_BGCOLOR_FOR_LIGHT;
  };

  const toggleVisibility = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    result: any
  ) => {
    if (result._group.visible) {
      // => not visible
      viewer.setMeasurementVisibility(result.id, {
        text: false,
        measurement: false,
      });
      if (volumeListProps.selectedItem?.id === result.id) {
        viewer.setHeatMapVisibility(result.id, false);
      }
    } else {
      // => visible
      viewer.setMeasurementVisibility(result.id, {
        text: true,
        measurement: true,
      });
      if (
        volumeListProps.showHeatMap &&
        volumeListProps.selectedItem?.id === result.id
      ) {
        viewer.setHeatMapVisibility(result.id, true);
      }
      volumeListProps.setSelectedItem(result);
      volumeListProps.setOpenCalcResultDetailDialog(true);
    }
    volumeListProps.handleSetSelectedCalcResult([
      ...volumeListProps.selectedCalcResult,
    ]);
    selectedValBool = true;
    clickVisibility = e.currentTarget;
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };
  const customBackground = {
    background: theme.palette.base.modal,
  };
  const customText = {
    color: theme.palette.base.calcText,
  };

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: "270px",
        overflow: "auto",
        border: "1px solid",
        flex: 2,
      }}
    >
      <List style={{ padding: 0 }}>
        {volumeListProps.selectedCalcResult.map((result, idx) => {
          const isSelectedItem = result === volumeListProps.selectedItem;
          return (
            <ListItem
              key={result.id}
              component="div"
              sx={{
                height: "28px",
                width: "100%",
                marginBottom: "2px",
                padding: "0px 4px",
                backgroundColor: `${
                  isSelectedItem
                    ? selectedItemColor()
                    : customBackground.background
                }${isSelectedItem ? " !important" : ""}`,
                "&:nth-of-type(odd)": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              divider
            >
              <ListItemIcon
                className="eye"
                id={String(result.id)}
                ref={(e) => (visibilityArrayRef.current[idx] = e)}
                onClick={(e) => toggleVisibility(e, result)}
              >
                {result.getVisibility() ? (
                  <Icon style={customColor}>visibility_on</Icon>
                ) : (
                  <Icon style={customColor}>visibility_off</Icon>
                )}
              </ListItemIcon>
              <ListItemButton sx={{ padding: 0 }}>
                <ListItemText
                  primary={
                    <Typography sx={{ fontSize: isJapanese ? "" : "11px" }}>
                      {volumeListProps.resCalcType(result)}【
                      {resDistance(result)} {resUnit(result)}】
                    </Typography>
                  }
                  sx={{ color: customColor.color }}
                  ref={(e) => (listItemArrayRef.current[idx] = e)}
                  id={String(result.id)}
                  onClick={(e) => clickListItemEvent(e, result)}
                />
              </ListItemButton>
              {result.type === MeasurementType.VOLUME_CALCULATION && (
                <Q3DIconButton
                  onClick={() => {
                    volumeListProps.setSelectedItem(result);
                    volumeListProps.toggleSetShowHeatMap();
                  }}
                  icon={<HeatMapIcon height={24} width={24} />}
                  height={"24px"}
                  width={"24px"}
                  disabledBorder={true}
                />
              )}
              <MeasurementMainMenu
                key={result.id}
                id={result.id}
                deleteMeasurementResult={deleteMeasurementResult}
                type={result.type}
                onClick={() => {
                  volumeListProps.setSelectedItem(result);
                  volumeListProps.setEditedItem(result);
                }}
                handlePickType={(type: string) => {
                  volumeListProps.handlePickType(type, result);
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
